import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import api from 'src/api/BlancoClient';
import i18n from 'src/i18n';
import { compareFavoriteItem } from 'src/lib/catalogFavoriteSorter';
import { addQueryToSearchHistory } from 'src/lib/catalogSearchHistoryManipulator';
import { sendEvent } from 'src/lib/google-analytics';
import { extractInvalidChars } from 'src/lib/message-validator';
import {
  PersistentData,
  PersistentUserCatalogNoticeState,
  PersistentUserCatalogSearchState,
  PersistentUserCatalogTodayState
} from 'src/lib/PersistentData';
import {
  persistUserCatalogNoticeState,
  persistUserCatalogSearchHistoryState,
  persistUserCatalogTodayState
} from 'src/lib/storage';
import { shuffle } from 'src/lib/utils';
import {
  CatalogCategory,
  CeilingsCategory,
  FashionCategory,
  FlooringCategory,
  FurnitureCategory,
  HousewaresCategory,
  MiscellaneousCategory,
  RugsCategory,
  WallpaperCategory
} from 'src/models/catalog/CatalogCategory';
import { CatalogItem } from 'src/models/catalog/CatalogItem';
import {
  CatalogItemWithKey,
  convertToDefault,
  createDefaultList,
  createListByKeys
} from 'src/models/catalog/CatalogItemWithKey';
import { CatalogFilterCondition } from 'src/models/catalog/filter/CatalogFilterCondition';
import { filterCatalogItems } from 'src/models/catalog/filter/filterCatalogItems';
import { catalogSearchByKeyword } from 'src/models/catalog/search/catalogSearchByKeyword';
import { TodayItemData } from 'src/models/catalog/today/TodayItemData';
import { NormalizedData } from 'src/models/common/NormalizedData';
import { CatalogData, CatalogErrorResponse } from 'src/worker/worker';
import { RootState } from '.';
import Worker from '../worker';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RequestState } from './utils/RequestState';

export type CatalogErrorType = 'loading-failed' | 'unstable-network' | 'unknown';
type CatalogSearchErrorType = { queryIsTooLong?: boolean; invalidChar?: boolean };
export type CatalogFavoriteRegisterErrorType = CatalogErrorType | 'over-registration-limit';

type FilterState = {
  condition: CatalogFilterCondition;
  data: RequestState<CatalogItemWithKey[], CatalogErrorType>;
};

export type SubCategoryLists = { furniture: FurnitureCategory[]; fashion: FashionCategory[] };
export type SubSubCategoryLists = {
  housewares: HousewaresCategory[];
  miscellaneous: MiscellaneousCategory[];
  ceilings: CeilingsCategory[];
  wallpaper: WallpaperCategory[];
  flooring: FlooringCategory[];
  rugs: RugsCategory[];
};

export type CatalogCategoryLists = {
  category: CatalogCategory[];
  subCategory: SubCategoryLists;
  subSubCategory: SubSubCategoryLists;
};

type SearchState = { query: string; data: RequestState<CatalogItemWithKey[], CatalogErrorType> };

export interface CatalogState {
  all?: RequestState<
    {
      data: NormalizedData<CatalogItem>;
      filteredCategoryLists: CatalogCategoryLists;
      updatedAt: number;
      currentYear: number;
    },
    CatalogErrorType
  >;
  userTodayStates?: { [userId: string]: PersistentUserCatalogTodayState };
  userNoticeStates?: { [userId: string]: PersistentUserCatalogNoticeState };
  userHistoryStates?: { [userId: string]: PersistentUserCatalogSearchState };
  today?: {
    items: CatalogItemWithKey[];
  };
  filter: {
    results?: FilterState;
  };
  search: {
    query?: string;
    queryError?: CatalogSearchErrorType;
    results?: SearchState;
  };
  favorite?: RequestState<CatalogItemWithKey[], CatalogErrorType>;
  favoriteRegister?: RequestState<Record<string, unknown>, CatalogFavoriteRegisterErrorType>;
  latest?: RequestState<CatalogItemWithKey[], CatalogErrorType>;
  isOpenAndEnterRemakeShop?: boolean;
  isUnlockRegulation?: boolean;
  isUnlockFenceRemake?: boolean;
}

const textCountLimit = 100; // 検索クエリの上限
const numberOfSearchHistory = 3; // 検索履歴の保持数

const initialState: CatalogState = {
  search: {},
  filter: {}
};

const slice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    persistentDataRestored: (state, { payload }: PayloadAction<PersistentData>) => {
      state.userTodayStates = payload.userCatalogTodayStates;
      state.userHistoryStates = payload.userCatalogSearchStates;
      state.userNoticeStates = payload.userCatalogNoticeStates;
    },

    getCatalogData: state => {
      state.all = { state: 'pending' };
      state.search = {};
      state.filter = {};
    },

    getCatalogDataSucceeded: (
      state,
      {
        payload
      }: PayloadAction<{
        catalogData: CatalogData;
        todayItems: CatalogItemWithKey[];
        currentYear: number;
      }>
    ) => {
      const { catalogData, todayItems, currentYear } = payload;
      const {
        data,
        categories,
        favoriteItems,
        latestItems,
        updatedAt,
        isOpenAndEnterRemakeShop,
        isUnlockRegulation,
        isUnlockFenceRemake
      } = catalogData;

      state.all = {
        state: 'ok',
        result: {
          data,
          filteredCategoryLists: categories,
          updatedAt,
          currentYear
        }
      };
      state.today = { items: todayItems };

      const filterResult = createDefaultList(data);
      state.filter = {
        results: {
          condition: {},
          data: { state: 'ok', result: filterResult }
        }
      };
      state.latest = { state: 'ok', result: latestItems };
      state.favorite = { state: 'ok', result: favoriteItems };
      state.isOpenAndEnterRemakeShop = isOpenAndEnterRemakeShop;
      state.isUnlockRegulation = isUnlockRegulation;
      state.isUnlockFenceRemake = isUnlockFenceRemake;
    },

    getCatalogDataFailed: (state, { payload }: PayloadAction<CatalogErrorType>) => {
      state.all = { state: 'error', error: payload };
    },

    updateSearchKeyword: (state, { payload }: PayloadAction<string>) => {
      state.search.query = payload;
      const invalidChars = extractInvalidChars(payload);
      const count = Array.from(payload).length;
      const queryTooLong = count > textCountLimit;
      if (!!invalidChars) {
        state.search.queryError = { ...state.search.queryError, invalidChar: true };
      } else {
        state.search.queryError = { ...state.search.queryError, invalidChar: false };
      }
      if (queryTooLong) {
        state.search.queryError = { ...state.search.queryError, queryIsTooLong: true };
      } else {
        state.search.queryError = { ...state.search.queryError, queryIsTooLong: false };
      }
    },

    filterItems: (state, { payload }: PayloadAction<CatalogFilterCondition>) => {
      state.filter.results = {
        condition: payload,
        data: { state: 'pending' }
      };
    },

    filterItemsSucceeded: (
      state,
      { payload }: PayloadAction<{ condition: CatalogFilterCondition; items: CatalogItemWithKey[] }>
    ) => {
      state.filter.results = {
        condition: payload.condition,
        data: { state: 'ok', result: payload.items }
      };
    },

    searchItems: (state, { payload }: PayloadAction<string>) => {
      state.search.results = {
        query: payload,
        data: { state: 'pending' }
      };
    },

    searchItemsSucceeded: (
      state,
      {
        payload
      }: PayloadAction<{
        query: string;
        items: CatalogItemWithKey[];
        userId: string;
        searchHistory: PersistentUserCatalogSearchState['history'];
      }>
    ) => {
      const { userId, searchHistory } = payload;
      if (!state.userHistoryStates) {
        state.userHistoryStates = { [userId]: { history: [] } };
      }
      const current = state.userHistoryStates[userId];
      state.search.results = {
        query: payload.query,
        data: { state: 'ok', result: payload.items }
      };
      state.userHistoryStates[userId] = { ...current, history: searchHistory };
    },

    favoriteItem: (state, _: PayloadAction<CatalogItemWithKey>) => {
      state.favoriteRegister = { state: 'pending' };
    },

    favoriteItemSucceeded: (state, { payload }: PayloadAction<CatalogItemWithKey>) => {
      state.favoriteRegister = { state: 'ok', result: {} };
      if (state.favorite?.state === 'ok') {
        const updatedFavorites = [payload, ...state.favorite.result];
        updatedFavorites.sort(compareFavoriteItem);
        state.favorite.result = updatedFavorites;
      }
    },

    favoriteItemFailed: (state, { payload }: PayloadAction<CatalogFavoriteRegisterErrorType>) => {
      state.favoriteRegister = { state: 'error', error: payload };
    },

    unfavoriteItem: (state, _: PayloadAction<CatalogItemWithKey>) => {
      state.favoriteRegister = { state: 'pending' };
    },

    unfavoriteItemSucceeded: (state, { payload }: PayloadAction<CatalogItemWithKey>) => {
      state.favoriteRegister = { state: 'ok', result: {} };
      if (state.favorite?.state === 'ok') {
        const { key } = payload;
        state.favorite.result = state.favorite.result.filter(
          item => item.key.unique_id !== key.unique_id || item.key.rebody_index !== key.rebody_index
        );
      }
    },

    unfavoriteItemFailed: (state, { payload }: PayloadAction<CatalogFavoriteRegisterErrorType>) => {
      state.favoriteRegister = { state: 'error', error: payload };
    },

    showedDisplayedPriceNotice: () => {
      /* do nothing */
    },

    setNotifiedDisplayedPriceFlag: (state, { payload }: PayloadAction<string>) => {
      const userId = payload;
      const current = (state.userNoticeStates && state.userNoticeStates[userId]) ?? {};
      const updated = { ...current, notifiedDisplayedPrice: true };
      state.userNoticeStates = { ...state.userNoticeStates, ...{ [userId]: updated } };
    },

    reset: state => {
      state.all = undefined;
      state.filter = {};
      state.search = {};
      state.favorite = undefined;
      state.favoriteRegister = undefined;
      state.latest = undefined;
    },

    resetFilter: state => {
      state.search = {};
      state.favoriteRegister = undefined;
      if (state.all?.state === 'ok') {
        const filterResult = createDefaultList(state.all.result.data);
        state.filter.results = {
          condition: {},
          data: { state: 'ok', result: filterResult }
        };
      }
    },
    resetFavoriteRegister: state => {
      state.favoriteRegister = undefined;
    }
  }
});

export default slice.reducer;

const {
  persistentDataRestored,
  getCatalogData,
  updateSearchKeyword,
  filterItems,
  searchItems,
  favoriteItem,
  unfavoriteItem,
  showedDisplayedPriceNotice,
  reset,
  resetFilter,
  resetFavoriteRegister,
  ...internalActions
} = slice.actions;

export const actions = {
  persistentDataRestored,
  getCatalogData,
  updateSearchKeyword,
  filterItems,
  searchItems,
  favoriteItem,
  unfavoriteItem,
  showedDisplayedPriceNotice,
  reset,
  resetFilter,
  resetFavoriteRegister
};

const {
  getCatalogDataSucceeded,
  getCatalogDataFailed,
  filterItemsSucceeded,
  searchItemsSucceeded,
  favoriteItemSucceeded,
  favoriteItemFailed,
  unfavoriteItemSucceeded,
  unfavoriteItemFailed,
  setNotifiedDisplayedPriceFlag
} = internalActions;

export function* catalogSaga() {
  yield takeLatest(getCatalogData.type, getCatalogDataSaga);
  yield takeLatest(filterItems.type, filterItemsSaga);
  yield takeLatest(searchItems.type, searchItemsSaga);
  yield takeLatest(favoriteItem.type, favoriteItemSaga);
  yield takeLatest(unfavoriteItem.type, unfavoriteItemSaga);
  yield takeLatest(showedDisplayedPriceNotice.type, showedDisplayedPriceNoticeSaga);
}

function* getCatalogDataSaga() {
  const catalogState: CatalogState = yield select((state: RootState) => state.catalog);
  const userState: UserState = yield select((state: RootState) => state.user);
  const { all, userTodayStates } = catalogState;
  const { selectedUserId } = userState;

  if (!selectedUserId) {
    return;
  }

  // 既にデータが取得済みであれば何もしない
  if (all?.state === 'ok') {
    return;
  }

  try {
    const baid: string = yield call(getFreshToken);
    const language = i18n.language;
    const currentYear = new Date().getFullYear();
    const currentYearString = currentYear.toString();
    const instance = new Worker();
    const result: CatalogData | CatalogErrorResponse = yield call(
      instance.getCatalogData,
      baid,
      language,
      currentYearString
    );

    if (!result.hasOwnProperty('allItems')) {
      // resultがallItemsプロパティを持っていない場合はCatalogErrorResponseが返って来ているとみなす
      const errorResponse = result as CatalogErrorResponse;

      // CatalogErrorResponseをみてcatch文と同様のエラー処理を行う
      // resultがCatalogErrorResponseでなかった場合はerrorTypeを持たないのでif文を抜ける
      switch (errorResponse.errorType) {
        case 'server':
          yield put(userActions.causeServerError(errorResponse.errorCode));
          return;
        case 'unstable-network':
          yield put(getCatalogDataFailed('unstable-network'));
          return;
        case 'client':
          yield put(getCatalogDataFailed('loading-failed'));
          return;
      }
    }
    const catalogData = result as CatalogData;

    let todayItems: CatalogItemWithKey[] = [];

    // 「今日のアイテム」がローカルキャッシュがある場合には使用する

    if (userTodayStates && userTodayStates[selectedUserId]) {
      const { itemKeys, createdAt } = userTodayStates[selectedUserId];
      const now = new Date();
      const createdDate = new Date(createdAt * 1000);
      if (
        now.getFullYear() === createdDate.getFullYear() &&
        now.getMonth() === createdDate.getMonth() &&
        now.getDate() === createdDate.getDate()
      ) {
        todayItems = createListByKeys(itemKeys, catalogData.data);
      }
    }

    if (todayItems.length === 0) {
      const todayItemData: TodayItemData | undefined = yield call(
        instance.createTodayItemData,
        catalogData.data
      );

      if (todayItemData) {
        todayItems = todayItemData.items;
      } else if (catalogData.latestItems.length >= 4) {
        // アイテムがつくれなかったら新着アイテムで代替する
        todayItems = shuffle([...catalogData.latestItems].slice(0, 12));
      }

      try {
        if (todayItems.length > 0) {
          yield call(persistUserCatalogTodayState, selectedUserId, {
            itemKeys: todayItems.map(item => item.key),
            createdAt: Math.floor(new Date().getTime() / 1000)
          });

          const label = todayItemData?.type ?? 'latest';
          sendEvent('catalog_bg_items', 'create', label);
        } else {
          sendEvent('catalog_bg_items', 'create', 'none');
        }
      } catch {}
    }

    yield put(getCatalogDataSucceeded({ catalogData, todayItems, currentYear }));
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getCatalogDataFailed('unstable-network'));
    } else {
      yield put(getCatalogDataFailed('loading-failed'));
    }
  }
}

function* filterItemsSaga(action: {
  type: typeof filterItems.type;
  payload: CatalogFilterCondition;
}) {
  const { all, isOpenAndEnterRemakeShop, isUnlockFenceRemake }: CatalogState = yield select(
    (state: RootState) => state.catalog
  );
  if (all?.state !== 'ok') {
    return;
  }

  const condition = action.payload;
  const { byId, allIds } = all.result.data;
  const allItems = allIds.map(id => byId[id]);
  const filterResults = filterCatalogItems(
    condition,
    allItems,
    isOpenAndEnterRemakeShop,
    isUnlockFenceRemake
  );

  yield put(filterItemsSucceeded({ condition, items: filterResults }));
}

function* searchItemsSaga(action: { type: typeof searchItems.type; payload: string }) {
  const { all, userHistoryStates }: CatalogState = yield select(
    (state: RootState) => state.catalog
  );
  const { selectedUserId }: UserState = yield select((state: RootState) => state.user);

  if (all?.state !== 'ok' || !selectedUserId) {
    return;
  }

  let userCatalogState: PersistentUserCatalogSearchState;
  if (!userHistoryStates || !userHistoryStates[selectedUserId]) {
    userCatalogState = { history: [] };
  } else {
    userCatalogState = userHistoryStates[selectedUserId];
  }
  const { history: searchHistory } = userCatalogState;

  const query = action.payload;
  const { byId, allIds } = all.result.data;
  const allItems = allIds.map(id => byId[id]);
  const searchResults = catalogSearchByKeyword(query, allItems);
  const items = searchResults.map(item => convertToDefault(item));

  const historyResponse = addQueryToSearchHistory(searchHistory, query);
  const updatedSearchHistory = historyResponse.slice(0, numberOfSearchHistory);
  yield call(persistUserCatalogSearchHistoryState, selectedUserId, updatedSearchHistory);
  yield put(
    searchItemsSucceeded({
      query,
      items,
      userId: selectedUserId,
      searchHistory: updatedSearchHistory
    })
  );
}

function* favoriteItemSaga(action: ReturnType<typeof favoriteItem>) {
  const catalogState: CatalogState = yield select((state: RootState) => state.catalog);
  const { favorite } = catalogState;
  const addItem = action.payload;
  const { key } = addItem;

  if (favorite?.state !== 'ok') {
    yield put(favoriteItemFailed('unknown'));
    return;
  }

  // rebody_patternが存在するアイテムでkeyにrebody_indexがない場合はエラーにする
  if (addItem.data.type === 'body-variable' && addItem.key.rebody_index === undefined) {
    yield put(favoriteItemFailed('unknown'));
    return;
  }

  try {
    const baid: string = yield call(getFreshToken);
    yield call(api.putCatalogFavorite, key, baid);
    yield put(favoriteItemSucceeded(addItem));
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(favoriteItemFailed('unstable-network'));
    } else {
      if (error.response.data?.code) {
        const { code } = error.response.data;
        const favoriteRegisterError = getCatalogFavoriteRegisterErrorType(code);
        yield put(favoriteItemFailed(favoriteRegisterError));
      } else {
        yield put(favoriteItemFailed('unknown'));
      }
    }
  }
}

function* unfavoriteItemSaga(action: ReturnType<typeof unfavoriteItem>) {
  const catalogState: CatalogState = yield select((state: RootState) => state.catalog);
  const { favorite } = catalogState;
  const deleteItem = action.payload;
  const { key } = deleteItem;

  if (favorite?.state !== 'ok') {
    yield put(unfavoriteItemFailed('unknown'));
    return;
  }

  // rebody_patternが存在するアイテムでkeyにrebody_indexがない場合はエラーにする
  if (deleteItem.data.type === 'body-variable' && deleteItem.key.rebody_index === undefined) {
    yield put(unfavoriteItemFailed('unknown'));
    return;
  }

  try {
    const baid: string = yield call(getFreshToken);
    yield call(api.deleteCatalogFavorite, key, baid);
    const deleted = favorite.result.find(
      item => item.key.unique_id === key.unique_id && item.key.rebody_index === key.rebody_index
    );
    if (deleted) {
      yield put(unfavoriteItemSucceeded(deleted));
    } else {
      yield put(unfavoriteItemFailed('unknown'));
    }
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(favoriteItemFailed('unstable-network'));
    } else {
      if (error.response.data?.code) {
        const { code } = error.response.data;
        const favoriteRegisterError = getCatalogFavoriteRegisterErrorType(code);
        yield put(favoriteItemFailed(favoriteRegisterError));
      } else {
        yield put(favoriteItemFailed('unknown'));
      }
    }
  }
}

function* showedDisplayedPriceNoticeSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { selectedUserId: userId } = userState;

  if (!userId) {
    return;
  }

  try {
    yield call(persistUserCatalogNoticeState, userId, { notifiedDisplayedPrice: true });
  } catch {}
  yield put(setNotifiedDisplayedPriceFlag(userId));
}

// helper function
function getCatalogFavoriteRegisterErrorType(code?: string) {
  switch (code) {
    case '1302':
      return 'over-registration-limit';
    default:
      return 'unknown';
  }
}
